import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { IS_NATIVE_APP } from "../config/appTarget.js";
import { apiClient } from "../lib/apiClient.js";

/**
 * On native (iOS/Android) shells, tells the backend "this user has the app" once
 * per session after they authenticate. The backend upgrades free users to the
 * `app` tier (50 swipes/day). No-op on web and after the first successful call.
 */
export function useAppInstallUpgrade(): void {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const doneRef = useRef(false);

  useEffect(() => {
    if (!IS_NATIVE_APP || !isAuthenticated || doneRef.current) return;
    doneRef.current = true;

    (async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            scope: "openid profile email",
          },
        });
        await apiClient.registerAppInstall({
          Authorization: `Bearer ${token}`,
        });
      } catch {
        // Best-effort: a failed upgrade just means the user keeps the free tier
        // until the next session retries. Don't surface an error.
        doneRef.current = false;
      }
    })();
  }, [isAuthenticated, getAccessTokenSilently]);
}
